import React, { useState, useEffect } from 'react';
import Nprogress from 'nprogress';
import ReactPlaceholder from 'react-placeholder';
import 'nprogress/nprogress.css';
import 'react-placeholder/lib/reactPlaceholder.css';
import CircularProgress from '../components/CircularProgress';
import { PageTrack } from 'containers/Services/AnalyticsProvider';
import Fade from '@mui/material/Fade';

/**
 * A higher-order component for loading components asynchronously with loading states and page tracking
 */
export default function asyncComponent<Props = any>(
  importComponent: () => Promise<{ default: React.ComponentType<Props> }>
): React.FC<Props> {
  const getQueryParameter = (url: URL, parameter: string): string | null => {
    const queryString = new URLSearchParams(url.search);
    return queryString.get(parameter);
  };

  const getPathSegment = (url: URL, index: number): string => {
    return url.pathname.split('/')[index];
  };

  const getPageName = (): string => {
    const url = new URL(window.location.href);
    const tabName = getQueryParameter(url, 'tab');
    const sectionName = getQueryParameter(url, 'section');
    const appName = getPathSegment(url, 1);
    const subPageName = getPathSegment(url, 2);

    let finalPageName = appName;
    if (tabName) finalPageName += `_${tabName}`;
    if (sectionName) finalPageName += `_${sectionName}`;
    else if (!tabName && subPageName) finalPageName += `_${subPageName}`;

    return finalPageName;
  };

  const AsyncFunc: React.FC<Props> = (props) => {
    const [component, setComponent] = useState<React.ReactNode | null>(null);
    const [mounted, setMounted] = useState<boolean>(false);
    // Use ref for tracking mount state to avoid closure issues in async code
    const mountedRef = React.useRef<boolean>(false);

    useEffect(() => {
      // Start progress bar on mount (equivalent to componentWillMount)
      Nprogress.start();
      
      // Set mounted flags
      setMounted(true);
      mountedRef.current = true;

      // Load component asynchronously
      const loadComponent = async () => {
        try {
          const { default: Component } = await importComponent();

          // Page tracking
          PageTrack(getPageName());
          
          // Complete the progress bar
          Nprogress.done();
          
          // Only update state if component is still mounted
          if (mountedRef.current) {
            // Wrap the component with the MUI Fade animation
            setComponent(
              <Fade in={true} timeout={400}>
                <div>
                  <Component {...(props as any)} />
                </div>
              </Fade>
            );
          }
        } catch (error) {
          console.error('Error loading async component:', error);
          Nprogress.done();
        }
      };
      
      loadComponent();
      
      // Cleanup function (equivalent to componentWillUnmount)
      return () => {
        setMounted(false);
        mountedRef.current = false;
      };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [importComponent, props]); // Include importComponent and props as dependencies

    // Loading placeholder
    const loadingView = (
      <div className="loader-view" style={{ height: 'calc(100vh - 200px)' }}>
        <CircularProgress className={undefined} />
      </div>
    );

    return (
      <>
        {component || loadingView}
      </>
    );
  };

  return AsyncFunc;
}
